import React, { useMemo } from "react";
import { Modal } from "antd";
import { WarningOutlined, CheckCircleOutlined, CalendarOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { svg } from "../../../../../Assets/svg";
import { formatCreatedLeavesSummary } from "../../../../../helpers/moment";

// Calendar View imports (currently commented out)
// import { Calendar, Tooltip } from "antd";
// import { useState, useEffect } from "react";

dayjs.extend(customParseFormat);

export interface AlreadyAppliedLeaveItem {
  date?: string;
  dateRange?: string;
  dates?: string[];
  typeName?: string;
  typeKind?: string;
  status?: string;
  slotStr?: string;
  countDays?: number;
  note?: string;
}

export interface AlreadyAppliedTimeOffModalProps {
  visible: boolean;
  onClose: () => void;
  alreadyAppliedLeaves: AlreadyAppliedLeaveItem[];
  createdCount?: number;
  createdDateRange?: string;
  createdRequests?: any[];
  employeeName?: string;
}

export const AlreadyAppliedTimeOffModal: React.FC<AlreadyAppliedTimeOffModalProps> = ({
  visible,
  onClose,
  alreadyAppliedLeaves = [],
  createdCount,
  createdDateRange,
  createdRequests,
  employeeName,
}) => {
  const summary = useMemo(() => {
    if (createdDateRange) {
      return {
        count: createdCount ?? 0,
        dateRange: createdDateRange,
      };
    }
    if (createdRequests && (Array.isArray(createdRequests) ? createdRequests.length > 0 : Boolean(createdRequests))) {
      const parsed = formatCreatedLeavesSummary(createdRequests);
      return {
        count: createdCount ?? parsed.createdCount,
        dateRange: parsed.createdDateRange,
      };
    }
    return {
      count: createdCount ?? 0,
      dateRange: "",
    };
  }, [createdCount, createdDateRange, createdRequests]);

  const effectiveCount = summary.count;
  const effectiveDateRange = summary.dateRange;

  const parseDateStr = (dateStr?: string): Dayjs | null => {
    if (!dateStr) return null;
    const trimmed = dateStr.trim();
    const d1 = dayjs(trimmed, "DD-MM-YYYY");
    if (d1.isValid()) return d1;
    const parts = trimmed.split(/[-/]/);
    if (parts.length === 3) {
      const d = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10) - 1;
      const y = parseInt(parts[2], 10);
      if (!isNaN(d) && !isNaN(m) && !isNaN(y)) {
        return dayjs().year(y).month(m).date(d).startOf("day");
      }
    }
    return null;
  };

  // Expand leaves so every single date is displayed as an individual entity rather than a date range
  const individualEntries = useMemo(() => {
    const list: Array<{
      date: string;
      typeName?: string;
      typeKind?: string;
      status?: string;
      slotStr?: string;
      countDays: number;
      note?: string;
    }> = [];

    for (const item of alreadyAppliedLeaves) {
      if (Array.isArray(item.dates) && item.dates.length > 0) {
        for (const d of item.dates) {
          list.push({
            date: d,
            typeName: item.typeName,
            typeKind: item.typeKind,
            status: item.status,
            slotStr: item.slotStr,
            countDays: 1,
            note: item.note,
          });
        }
      } else if (item.dateRange && item.dateRange.includes("-")) {
        const [sStr, eStr] = item.dateRange.split(/[-–—]/).map((s) => s.trim());
        const s = parseDateStr(sStr);
        const e = parseDateStr(eStr || sStr);
        if (s && e) {
          let curr = s.clone();
          while (curr.isBefore(e) || curr.isSame(e, "day")) {
            list.push({
              date: curr.format("DD-MM-YYYY"),
              typeName: item.typeName,
              typeKind: item.typeKind,
              status: item.status,
              slotStr: item.slotStr,
              countDays: 1,
              note: item.note,
            });
            curr = curr.add(1, "day");
          }
        } else {
          list.push({
            date: item.dateRange,
            typeName: item.typeName,
            typeKind: item.typeKind,
            status: item.status,
            slotStr: item.slotStr,
            countDays: 1,
            note: item.note,
          });
        }
      } else {
        list.push({
          date: item.date || item.dateRange || "Unknown Date",
          typeName: item.typeName,
          typeKind: item.typeKind,
          status: item.status,
          slotStr: item.slotStr,
          countDays: item.countDays || 1,
          note: item.note,
        });
      }
    }
    return list;
  }, [alreadyAppliedLeaves]);

  const totalSkippedDays = individualEntries.reduce(
    (acc, curr) => acc + (curr.countDays || 1),
    0
  );

  /*
  // Calendar View helper logic (Commented out right now)
  const initialDate = useMemo(() => {
    for (const item of alreadyAppliedLeaves) {
      if (item.dates && item.dates.length > 0) {
        for (const d of item.dates) {
          const parsed = parseDateStr(d);
          if (parsed) return parsed;
        }
      }
      if (item.dateRange) {
        const firstStr = item.dateRange.split(/[-–—]/)[0]?.trim();
        const parsed = parseDateStr(firstStr);
        if (parsed) return parsed;
      }
    }
    return dayjs();
  }, [alreadyAppliedLeaves]);

  const [calendarValue, setCalendarValue] = useState<Dayjs>(initialDate);

  useEffect(() => {
    setCalendarValue(initialDate);
  }, [initialDate]);

  const dateMap = useMemo(() => {
    const map = new Map<string, { typeName: string; status: string }>();
    for (const item of alreadyAppliedLeaves) {
      if (Array.isArray(item.dates)) {
        for (const d of item.dates) {
          const parsed = parseDateStr(d);
          if (parsed) {
            map.set(parsed.format("YYYY-MM-DD"), {
              typeName: item.typeName || "Time Off",
              status: item.status || "Applied",
            });
          }
        }
      } else if (item.dateRange) {
        const [sStr, eStr] = item.dateRange.split(/[-–—]/).map((s) => s.trim());
        const s = parseDateStr(sStr);
        const e = parseDateStr(eStr || sStr);
        if (s && e) {
          let curr = s.clone();
          while (curr.isBefore(e) || curr.isSame(e, "day")) {
            map.set(curr.format("YYYY-MM-DD"), {
              typeName: item.typeName || "Time Off",
              status: item.status || "Applied",
            });
            curr = curr.add(1, "day");
          }
        }
      }
    }
    return map;
  }, [alreadyAppliedLeaves]);

  const renderDateCell = (current: Dayjs, info: { type: string; originNode: React.ReactNode }) => {
    if (info.type !== "date") return info.originNode;

    const key = current.format("YYYY-MM-DD");
    const appliedInfo = dateMap.get(key);

    if (appliedInfo) {
      const tooltipContent = (
        <div style={{ textAlign: "center" }}>
          <div className="fw-bold">{current.format("DD-MM-YYYY")}</div>
          <div>{appliedInfo.typeName} ({appliedInfo.status})</div>
          <div className="small text-warning">Already applied time off</div>
        </div>
      );

      return (
        <Tooltip title={tooltipContent}>
          <div
            className="ant-picker-cell-inner ant-picker-calendar-date"
            style={{
              backgroundColor: "#fffbe6",
              border: "1.5px solid #faad14",
              borderRadius: "6px",
              color: "#d46b08",
              fontWeight: "bold",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              minHeight: "36px",
              margin: "1px 0",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
          >
            <div style={{ fontSize: "12px", lineHeight: "1.2" }}>
              {current.date()}
            </div>
            <div
              style={{
                fontSize: "9px",
                lineHeight: "1.1",
                color: "#d46b08",
                marginTop: "2px",
                padding: "0 2px",
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontWeight: "normal",
              }}
            >
              {appliedInfo.typeName}
            </div>
          </div>
        </Tooltip>
      );
    }

    return info.originNode;
  };
  */

  if (!visible) return null;

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={640}
      closeIcon={svg?.closeIcon}
      centered
      className="commonModalClass already-applied-time-off-modal"
    >
      <div className="modal-content border-0 p-0 pt-0 page-form">
        <div className="modal-header border-0 pb-1 mb-3 mt-2">
          <h1 className="modal-title fs-4 fw-bolder mx-auto text-dark text-center">
            Already Applied Time Off
          </h1>
        </div>

        <div
          className="modal-body pb-0 px-2 px-md-3"
          style={{ maxHeight: "75vh", overflowY: "auto" }}
        >
          {/* Success summary message */}
          <div
            className="p-3 mb-3 rounded-3 d-flex align-items-center gap-3"
            style={{
              backgroundColor: "#f6ffed",
              border: "1px solid #b7eb8f",
            }}
          >
            <CheckCircleOutlined style={{ color: "#52c41a", fontSize: "22px" }} />
            <div>
              <div className="fw-bold text-success fs-6">
                Time Off Created Successfully
              </div>
              <div className="text-secondary fs-7">
                {effectiveCount && effectiveCount > 0 ? (
                  <>
                    <strong>
                      {effectiveCount} {effectiveCount === 1 ? "day" : "days"}
                    </strong>
                    {effectiveDateRange ? ` (${effectiveDateRange})` : ""} booked for{" "}
                    <strong>{employeeName || "the employee"}</strong>.
                  </>
                ) : (
                  "The available dates have been successfully booked."
                )}
              </div>
            </div>
          </div>

          {/* Already Applied Leave Section */}
          <div
            className="p-3 rounded-3 mb-3"
            style={{
              backgroundColor: "#fffbe6",
              border: "1px solid #ffe58f",
              boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
            }}
          >
            <div className="d-flex align-items-center gap-2 mb-2">
              <WarningOutlined style={{ color: "#faad14", fontSize: "18px" }} />
              <span className="fw-bold text-dark fs-6">
                Already Applied Time Off Detected
              </span>
            </div>

            <div className="text-secondary fs-7 mb-3">
              The following date{totalSkippedDays === 1 ? "" : "s"} already had time off applied and {totalSkippedDays === 1 ? "was" : "were"} excluded from this new request:
            </div>

            <div className="d-flex flex-column gap-2 mb-3">
              {individualEntries.map((item, idx) => (
                <div
                  key={idx}
                  className="d-flex flex-wrap align-items-center justify-content-between p-2 rounded-2 bg-white border"
                >
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="fw-semibold text-dark fs-7 d-flex align-items-center">
                      <CalendarOutlined className="text-secondary me-1" />
                      {item.date}
                    </span>
                    <span className="badge bg-light text-secondary border fs-8">
                      {item.countDays} {item.countDays === 1 ? "day" : "days"}
                    </span>
                    {item.slotStr && (
                      <span className="badge bg-light text-dark border fs-8">
                        {item.slotStr}
                      </span>
                    )}
                    {item.typeName && (
                      <span
                        className="badge fs-8"
                        style={{
                          backgroundColor: "#e6f7ff",
                          color: "#096dd9",
                          border: "1px solid #91d5ff",
                        }}
                      >
                        {item.typeName}
                      </span>
                    )}
                  </div>
                  <div className="d-flex align-items-center gap-1 mt-1 mt-sm-0">
                    <span
                      className={`badge text-capitalize fs-8 ${
                        item.status === "approved"
                          ? "bg-success text-white"
                          : "bg-warning text-dark"
                      }`}
                    >
                      {item.status || "Applied"}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Mini Ant Design Calendar View (Commented out right now) */}
            {/* 
            <div
              className="p-2 bg-white rounded-3 border mb-2"
              style={{
                boxShadow: "0 1px 4px rgba(0,0,0,0.03)",
              }}
            >
              <div className="d-flex align-items-center justify-content-between px-2 pt-1 pb-2 border-bottom mb-2">
                <div className="d-flex align-items-center gap-2">
                  <CalendarOutlined style={{ color: "#faad14", fontSize: "15px" }} />
                  <span className="fw-bold text-dark fs-7">
                    Calendar View ({calendarValue.format("MMMM YYYY")})
                  </span>
                </div>
                <div className="d-flex align-items-center gap-1 fs-8 text-secondary">
                  <span
                    style={{
                      display: "inline-block",
                      width: "10px",
                      height: "10px",
                      backgroundColor: "#fffbe6",
                      border: "1.5px solid #faad14",
                      borderRadius: "2px",
                    }}
                  />
                  <span>Already Applied ({totalSkippedDays} {totalSkippedDays === 1 ? "day" : "days"})</span>
                </div>
              </div>

              <div className="antd-compact-calendar-wrapper">
                <Calendar
                  fullscreen={false}
                  value={calendarValue}
                  onChange={(val) => setCalendarValue(val)}
                  fullCellRender={renderDateCell}
                />
              </div>
            </div> 
            */}

            <div
              className="pt-2 mt-2 border-top d-flex justify-content-between align-items-center fs-7"
              style={{ color: "#666" }}
            >
              <span>
                Total already applied:{" "}
                <strong className="text-dark">
                  {totalSkippedDays} {totalSkippedDays === 1 ? "day" : "days"}
                </strong>
              </span>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-center mt-3 pt-2 mb-4 pb-1">
          <button
            type="button"
            className="btn btn-primary fw-bold px-5 py-2 btn-submit-modal"
            onClick={onClose}
          >
            OK
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AlreadyAppliedTimeOffModal;
